import { Button } from '@affine/component';
import { AuthInput } from '@affine/component/auth-components';
import {
  AuthService,
  CaptchaService,
  type Server,
} from '@affine/core/modules/cloud';
import { MAGICNOTES_SERVER_NAME } from '@affine/core/modules/cloud/magicnotes';
import { UserFriendlyError } from '@affine/error';
import { FrameworkScope, useLiveData } from '@toeverything/infra';
import { useEffect, useRef, useState } from 'react';

import { Captcha } from './captcha';
import * as styles from './style.css';

export function ForgotPassword({
  server,
  email,
  onEmailChange,
  redirectUrl,
}: {
  server?: Server;
  email: string;
  onEmailChange: (value: string) => void;
  redirectUrl?: string;
}) {
  const auth = server?.scope.get(AuthService);
  const captcha = server?.scope.get(CaptchaService);
  const needCaptcha = useLiveData(captcha?.needCaptcha$);
  const verifyToken = useLiveData(captcha?.verifyToken$);
  const challenge = useLiveData(captcha?.challenge$);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string>();
  const submitting = useRef(false);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown(value => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = async () => {
    if (
      !auth ||
      submitting.current ||
      cooldown ||
      (needCaptcha && !verifyToken)
    )
      return;
    submitting.current = true;
    setLoading(true);
    setError(undefined);
    setSent(false);
    try {
      await auth.requestPasswordReset(
        email.trim(),
        verifyToken,
        challenge,
        redirectUrl
      );
      setSent(true);
      setCooldown(60);
    } catch (error) {
      const friendly = UserFriendlyError.fromAny(error);
      setError(
        friendly.is('REQUEST_ABORTED') || friendly.is('NETWORK_ERROR')
          ? 'Unable to connect. Please try again.'
          : friendly.message
      );
    } finally {
      captcha?.revalidate();
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <form
      className={styles.credentialsForm}
      onSubmit={event => {
        event.preventDefault();
        send().catch(console.error);
      }}
    >
      <p>
        Enter your account email and we’ll send you a link to reset your
        password.
      </p>
      <AuthInput
        label="Server"
        aria-label="Server"
        value={MAGICNOTES_SERVER_NAME}
        readOnly
        disabled
        tabIndex={-1}
      />
      <AuthInput
        label="Email"
        aria-label="Email"
        type="email"
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required
        value={email}
        disabled={loading}
        onChange={value => {
          onEmailChange(value);
          setSent(false);
          setError(undefined);
        }}
      />
      {server && needCaptcha && (
        <FrameworkScope scope={server.scope}>
          <Captcha />
        </FrameworkScope>
      )}
      {error && (
        <div role="alert" className={styles.signInError}>
          {error}
        </div>
      )}
      {sent && (
        <p role="status">
          If an account exists for this email, we’ve sent a password reset link.
          Check your inbox and spam folder.
        </p>
      )}
      <Button
        variant="primary"
        size="extraLarge"
        block
        loading={loading}
        disabled={
          !auth ||
          loading ||
          !!cooldown ||
          !email.trim() ||
          (!!needCaptcha && !verifyToken)
        }
      >
        {cooldown ? `Send again in ${cooldown}s` : 'Send reset link'}
      </Button>
    </form>
  );
}
